import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { Avatar, Stack, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from "@mui/material";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Opportunity as LegacyOpportunity } from "../../stores/opportunities/Opportunity";
import { OpportunityEntry as ApiOpportunity } from "randmar-api-client";
import { FormatDateFromString, FormatDateFromStringWithHours } from "../util/DateTimeUtil";

interface IProps {
  defaultRowsPerPage?: number;
  opportunities?: (LegacyOpportunity | ApiOpportunity)[];
}

const OpportunitiesGrid: React.FC<IProps> = ({ opportunities: rawOpportunities, defaultRowsPerPage }) => {
  const { applicationId } = useManufacturer();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 20);

  const isLegacy = (opp: LegacyOpportunity | ApiOpportunity): opp is LegacyOpportunity => 'bidNumber' in opp;

  const opportunities: ApiOpportunity[] = (rawOpportunities || []).map(opp => {
    if (isLegacy(opp)) {
      return {
        BidNumber: opp.bidNumber,
        Opportunity: opp.opportunity,
        Description: opp.description,
        StartDate: opp.startDate,
        EndDate: opp.endDate,
        LastUpdate: opp.lastUpdate,
        Active: opp.active,
        DefaultResellers: opp.defaultResellers,
        CanBeSetAsDefault: opp.canBeSetAsDefault,
        BidPrice: opp.bidPrice,
        BidPriceType: opp.bidPriceType,
        RandmarSKU: opp.randmarSKU,
      };
    }
    return opp;
  });

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isOpportunityActive = (opp: ApiOpportunity) => {
    if (!opp.EndDate) return false;
    const parsedEndDate = new Date(opp.EndDate);
    if (Number.isNaN(parsedEndDate.getTime())) return false;
    return Boolean(opp.Active) && parsedEndDate >= new Date();
  };

  return (
    <Card className="w-full">
      <div className="w-full overflow-auto rounded-[20px]">
        <Table>
          <TableHead sx={{ backgroundColor: "var(--ops-accent-muted)" }}>
            <TableRow>
              <TableCell align="center" sx={{ borderColor: "#582c83", color: "#582c83", fontWeight: 600 }}>Active</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83", color: "#582c83", fontWeight: 600 }}>Number & Opportunity</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83", color: "#582c83", fontWeight: 600 }}>Start Date</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83", color: "#582c83", fontWeight: 600 }}>End Date</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83", color: "#582c83", fontWeight: 600 }}>Last Update</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83", color: "#582c83", fontWeight: 600 }}>Default Resellers</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {opportunities
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"Opportunity" + index}>
                  <TableCell align="center">
                    {isOpportunityActive(row) ?
                      <ThumbsUp className="h-5 w-5 text-green-500" />
                      :
                      <ThumbsDown className="h-5 w-5 text-gray-500" />
                    }
                  </TableCell>
                  <TableCell align="center">
                    <Link to={Path.opportunity(applicationId, row.BidNumber || "")} style={{ marginRight: "10px" }}>
                      <b>{row.BidNumber}</b> {row.Opportunity}
                    </Link>
                  </TableCell>
                  <TableCell align="center">{FormatDateFromString(row.StartDate || undefined)}</TableCell>
                  <TableCell align="center">{FormatDateFromString(row.EndDate || undefined)}</TableCell>
                  <TableCell align="center">{FormatDateFromStringWithHours(row.LastUpdate || undefined)}</TableCell>
                  <TableCell align="center">
                    {(row.DefaultResellers === "" || !row.DefaultResellers) ?
                      "None"
                      :
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {row.DefaultResellers?.split(",").filter(dr => (dr !== "")).map(d => (
                          <Avatar title={d} sx={{ width: 24, height: 24, bgcolor: "#582c83" }} key={d}>{d.charAt(0)}</Avatar>
                        ))}
                      </Stack>
                    }
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          {opportunities.length > rowsPerPage &&
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={opportunities.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>}
        </Table>
      </div>
    </Card>
  );
}

export default OpportunitiesGrid;
